import { useState, useEffect, useCallback } from 'react';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' at ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const dueUrgency = (iso) => {
  if (!iso) return 'normal';
  const diff = new Date(iso) - new Date();
  if (diff < 0) return 'overdue';
  if (diff < 24 * 60 * 60 * 1000) return 'urgent';
  if (diff < 3 * 24 * 60 * 60 * 1000) return 'soon';
  return 'normal';
};

// ── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };
  const icons = {
    success: 'M5 13l4 4L19 7',
    error: 'M6 18L18 6M6 6l12 12',
  };

  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${styles[type]}`}>
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[type]} />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Submit Modal ────────────────────────────────────────────────────────────

const SubmitModal = ({ assignment, onSubmit, onCancel, loading }) => {
  const [submissionText, setSubmissionText] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [formError, setFormError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!submissionText.trim() && !attachmentUrl.trim()) {
      return setFormError('Please provide either a text answer or an attachment link.');
    }
    onSubmit({
      submission_text: submissionText.trim() || null,
      attachment_url: attachmentUrl.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-900">Submit Assignment</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
          )}

          {/* Assignment info */}
          <div className="p-4 bg-gray-50 rounded-xl space-y-1">
            <h4 className="font-medium text-gray-900">{assignment.title}</h4>
            {assignment.description && (
              <p className="text-sm text-gray-500">{assignment.description}</p>
            )}
            <p className="text-xs text-gray-400">
              Due: {formatDateTime(assignment.due_date)}
              {assignment.batch_name && ` · ${assignment.batch_name}`}
            </p>
            {assignment.attachment_url && (
              <a
                href={assignment.attachment_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-secondary hover:underline mt-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Assignment Attachment
              </a>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Answer</label>
            <textarea
              value={submissionText}
              onChange={(e) => { setSubmissionText(e.target.value); setFormError(''); }}
              rows={5}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm resize-none"
              placeholder="Type your answer here..."
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Google Drive Link (optional)</label>
            <input
              type="text"
              value={attachmentUrl}
              onChange={(e) => { setAttachmentUrl(e.target.value); setFormError(''); }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="https://drive.google.com/..."
            />
            <p className="text-xs text-gray-400 mt-1">Share your work via Google Drive, GitHub, or any public link</p>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

const StudentAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(null); // assignment being submitted
  const [submitLoading, setSubmitLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('all'); // all | pending | submitted | graded
  const [search, setSearch] = useState('');

  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await api.get('/api/assignments');
      setAssignments(res.data || []);
    } catch (err) {
      setError('Failed to load assignments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleSubmit = async (data) => {
    setSubmitLoading(true);
    try {
      await api.post(`/api/assignments/${submitting.id}/submit`, data);
      setSubmitting(null);
      showToast('Assignment submitted successfully!');
      setLoading(true);
      await fetchAssignments();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to submit assignment'), 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Determine submission status per assignment
  const getStatus = (a) => {
    if (a.my_submission_status === 'graded') return 'graded';
    if (a.my_submission_status === 'submitted') return 'submitted';
    return 'pending';
  };

  const filtered = assignments.filter((a) => {
    const status = getStatus(a);
    if (filter === 'pending' && status !== 'pending') return false;
    if (filter === 'submitted' && status !== 'submitted') return false;
    if (filter === 'graded' && status !== 'graded') return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (a.title || '').toLowerCase().includes(q) ||
        (a.batch_name || '').toLowerCase().includes(q) ||
        (a.class_title || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="skeleton h-8 w-48" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="skeleton h-5 w-40 mb-2" />
                <div className="skeleton h-4 w-64 mb-2" />
                <div className="skeleton h-4 w-32" />
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Assignments</h1>
            <p className="text-sm text-gray-500">{assignments.length} assignments</p>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search assignments..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
            />
          </div>
          <div className="flex gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'pending', label: 'Pending' },
              { key: 'submitted', label: 'Submitted' },
              { key: 'graded', label: 'Graded' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                  filter === f.key
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Assignment Cards */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-14 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">No assignments found</p>
              <p className="text-xs text-gray-400 mt-1">
                {search ? 'Try a different search term' : 'Assignments will appear here when assigned by your trainer'}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((a) => {
              const status = getStatus(a);
              const urgency = dueUrgency(a.due_date);
              const urgencyStyles = {
                overdue: 'text-red-700 bg-red-50 border-red-200',
                urgent: 'text-red-600 bg-red-50 border-red-200',
                soon: 'text-amber-600 bg-amber-50 border-amber-200',
                normal: 'text-gray-600 bg-gray-50 border-gray-200',
              };
              const urgencyLabel = {
                overdue: 'Overdue',
                urgent: 'Due today',
                soon: 'Due soon',
                normal: `Due ${formatDate(a.due_date)}`,
              };
              return (
                <div key={a.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                          {a.batch_name || 'Batch'}
                        </span>
                        {a.class_title && (
                          <span className="text-xs font-medium text-secondary bg-secondary/10 px-2.5 py-1 rounded-full">
                            {a.class_title}
                          </span>
                        )}
                        {status === 'pending' && (
                          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${urgencyStyles[urgency]}`}>
                            {urgencyLabel[urgency]}
                          </span>
                        )}
                        {status === 'submitted' && (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full border text-blue-700 bg-blue-50 border-blue-200">
                            Submitted
                          </span>
                        )}
                        {status === 'graded' && (
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full border text-emerald-700 bg-emerald-50 border-emerald-200">
                            Graded: {a.my_submission_grade || '—'}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 text-lg">{a.title}</h3>
                      {a.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{a.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                        <span>By {a.creator_name || 'Trainer'}</span>
                        <span>Due {formatDateTime(a.due_date)}</span>
                        {a.attachment_url && (
                          <a href={a.attachment_url} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Attachment
                          </a>
                        )}
                      </div>

                    </div>
                    <div className="flex-shrink-0">
                      {status === 'pending' ? (
                        <button
                          onClick={() => setSubmitting(a)}
                          className="px-5 py-2.5 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors"
                        >
                          Submit
                        </button>
                      ) : status === 'submitted' ? (
                        <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-50 text-blue-600 text-sm font-medium rounded-xl">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Awaiting Grade
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-50 text-emerald-700 text-sm font-bold rounded-xl">
                          {a.my_submission_grade || '—'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Submit Modal */}
      {submitting && (
        <SubmitModal
          assignment={submitting}
          onSubmit={handleSubmit}
          onCancel={() => setSubmitting(null)}
          loading={submitLoading}
        />
      )}
    </Layout>
  );
};

export default StudentAssignments;
